import { IFormValues } from '@/components/forms/address-step/FormAddress'
import {
  IBaseAddressType,
  TPostApiRequestType,
  IPostApiRequestTypeWithWarehouse
} from '@/api/create-address/type'
import { GetAddressResponseType } from '@/api/address/type'

type AddressEntity = {
  postal_code: string
  address: string
  details: string
  lat: number
  lng: number
  is_warehouse: boolean
  city: number
  province: number
}

type AddressApiData = {
  company_address: AddressEntity | null
  warehouse_address: AddressEntity | null
}

export const mapApiDataToFormValues = (
  data: GetAddressResponseType
): IFormValues => {
  const company = data.company_address
  const warehouse = data.warehouse_address

  return {
    provinces: company ? company.province : null,
    cities: company ? company.city : null,
    address: company?.address ?? '',
    detail: company?.details ?? '',
    postalCode: company?.postal_code ?? '',
    lat: company?.lat ?? 35.6997,
    lng: company?.lng ?? 51.3408,
    is_warehouse: warehouse?.is_warehouse ?? false,
    warehouseProvinces: warehouse ? warehouse.province : null,
    warehouseCities: warehouse ? warehouse.city : null,
    warehouseAddress: warehouse?.address ?? '',
    warehouseDetail: warehouse?.details ?? '',
    warehousePostalCode: warehouse?.postal_code ?? '',
    warehouseLat: warehouse?.lat ?? 35.6997,
    warehouseLng: warehouse?.lng ?? 51.3408
  }
}

export const getPatchData = (
  originalData: AddressApiData | undefined,
  values: IFormValues
): Partial<TPostApiRequestType> => {
  const patchData: Partial<TPostApiRequestType> = {}

  const compareFields = <T extends Partial<IBaseAddressType>>(
    original: AddressEntity,
    updated: T,
    excludeFields: string[] = []
  ): Partial<IBaseAddressType> => {
    const result: Partial<IBaseAddressType> = {}

    Object.entries(updated).forEach(([key, value]) => {
      if (excludeFields.includes(key)) return

      const originalValue = original[key as keyof AddressEntity]

      if (
        value !== undefined &&
        (originalValue !== value || (value === null && originalValue !== null))
      ) {
        result[key as keyof IBaseAddressType] = value as any
      }
    })

    return result
  }

  if (originalData?.company_address) {
    const companyUpdates = compareFields(
      originalData.company_address,
      {
        postal_code: values.postalCode ?? undefined,
        address: values.address ?? undefined,
        details: values.detail ?? undefined,
        lat: values.lat,
        lng: values.lng,
        city: values.cities ?? undefined
      },
      ['is_warehouse']
    )

    if (Object.keys(companyUpdates).length > 0) {
      patchData.company_address = {
        ...companyUpdates,
        is_warehouse: values.is_warehouse
      } as IBaseAddressType
    }
  }

  if (values.is_warehouse) {
    const warehouseUpdates = originalData?.warehouse_address
      ? compareFields(originalData.warehouse_address, {
          postal_code: values.warehousePostalCode ?? undefined,
          address: values.warehouseAddress ?? undefined,
          details: values.warehouseDetail ?? undefined,
          lat: values.warehouseLat,
          lng: values.warehouseLng,
          city: values.warehouseCities ?? undefined,
          is_warehouse: true
        })
      : {
          postal_code: values.warehousePostalCode,
          address: values.warehouseAddress,
          details: values.warehouseDetail,
          lat: values.warehouseLat,
          lng: values.warehouseLng,
          city: values.warehouseCities ?? null,
          is_warehouse: true
        }

    if (Object.keys(warehouseUpdates).length > 0) {
      ;(patchData as IPostApiRequestTypeWithWarehouse).warehouse_address =
        warehouseUpdates as IBaseAddressType
    }
  } else if (originalData?.warehouse_address && !values.is_warehouse) {
    ;(patchData as IPostApiRequestTypeWithWarehouse).warehouse_address = null
  }

  return patchData
}
